// @spec GHG-STR-010
/**
 * TanStack Query hooks for GHG activity data
 */

import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import type {
  ActivityEstimatePreviewInput,
  ActivityDataListFilters,
  ActivityDataResponse,
  CreateActivityDataInput,
  UpdateActivityDataInput,
} from '../types/activity-data';

import {
  createActivityData,
  deleteActivityData,
  fetchActivityData,
  fetchActivityDataEntry,
  fetchScope1ActivityDataSummary,
  fetchScope2ActivityDataSummary,
  fetchScope3ActivityDataSummary,
  previewActivityEstimate,
  updateActivityData,
} from './activity-data';
import { useGHGOrganizationId } from './use-ghg-organization-id';

export const activityDataKeys = {
  all: ['ghg-activity-data'] as const,
  lists: () => [...activityDataKeys.all, 'list'] as const,
  list: (orgId: string, filters: ActivityDataListFilters) =>
    [...activityDataKeys.lists(), orgId, filters] as const,
  summaries: () => [...activityDataKeys.all, 'summary'] as const,
  scope1Summary: (orgId: string, filters: ActivityDataListFilters) =>
    [...activityDataKeys.summaries(), 'scope1', orgId, filters] as const,
  scope2Summary: (orgId: string, filters: ActivityDataListFilters) =>
    [...activityDataKeys.summaries(), 'scope2', orgId, filters] as const,
  scope3Summary: (orgId: string, filters: ActivityDataListFilters) =>
    [...activityDataKeys.summaries(), 'scope3', orgId, filters] as const,
  details: () => [...activityDataKeys.all, 'detail'] as const,
  detail: (orgId: string, entryId: string) =>
    [...activityDataKeys.details(), orgId, entryId] as const,
  preview: (orgId: string, request: ActivityEstimatePreviewInput | null) =>
    [...activityDataKeys.all, 'preview', orgId, request] as const,
};

export function useActivityData(filters: ActivityDataListFilters) {
  const { getToken, isSignedIn } = useAuth();
  const organizationId = useGHGOrganizationId();

  const query = useQuery({
    queryKey: activityDataKeys.list(organizationId ?? '', filters),
    queryFn: async () => {
      if (!organizationId) {
        throw new Error('Organization not found');
      }
      return fetchActivityData(getToken, organizationId, filters);
    },
    enabled: (isSignedIn ?? false) && !!organizationId && !!(filters.reportingPeriodId || (filters.dateFrom && filters.dateTo)),
    staleTime: 30 * 1000,
  });

  return {
    entries: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    page: query.data?.page ?? 1,
    pageSize: query.data?.pageSize ?? 50,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useScope1ActivityDataSummary(filters: ActivityDataListFilters) {
  const { getToken, isSignedIn } = useAuth();
  const organizationId = useGHGOrganizationId();

  const query = useQuery({
    queryKey: activityDataKeys.scope1Summary(organizationId ?? '', filters),
    queryFn: async () => {
      if (!organizationId) {
        throw new Error('Organization not found');
      }
      return fetchScope1ActivityDataSummary(getToken, organizationId, filters);
    },
    enabled: (isSignedIn ?? false) && !!organizationId && !!(filters.reportingPeriodId || (filters.dateFrom && filters.dateTo)),
    staleTime: 30 * 1000,
  });

  return {
    summary: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useScope2ActivityDataSummary(filters: ActivityDataListFilters) {
  const { getToken, isSignedIn } = useAuth();
  const organizationId = useGHGOrganizationId();

  const query = useQuery({
    queryKey: activityDataKeys.scope2Summary(organizationId ?? '', filters),
    queryFn: async () => {
      if (!organizationId) {
        throw new Error('Organization not found');
      }
      return fetchScope2ActivityDataSummary(getToken, organizationId, filters);
    },
    enabled: (isSignedIn ?? false) && !!organizationId && !!(filters.reportingPeriodId || (filters.dateFrom && filters.dateTo)),
    staleTime: 30 * 1000,
  });

  return {
    summary: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useScope3ActivityDataSummary(filters: ActivityDataListFilters) {
  const { getToken, isSignedIn } = useAuth();
  const organizationId = useGHGOrganizationId();

  const query = useQuery({
    queryKey: activityDataKeys.scope3Summary(organizationId ?? '', filters),
    queryFn: async () => {
      if (!organizationId) {
        throw new Error('Organization not found');
      }
      return fetchScope3ActivityDataSummary(getToken, organizationId, filters);
    },
    enabled: (isSignedIn ?? false) && !!organizationId && !!(filters.reportingPeriodId || (filters.dateFrom && filters.dateTo)),
    staleTime: 30 * 1000,
  });

  return {
    summary: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useActivityDataEntry(entryId: string | undefined) {
  const { getToken, isSignedIn } = useAuth();
  const organizationId = useGHGOrganizationId();

  const query = useQuery({
    queryKey: activityDataKeys.detail(organizationId ?? '', entryId ?? ''),
    queryFn: async () => {
      if (!organizationId || !entryId) {
        throw new Error('Organization or entry ID not found');
      }
      return fetchActivityDataEntry(getToken, organizationId, entryId);
    },
    enabled: (isSignedIn ?? false) && !!organizationId && !!entryId,
    staleTime: 30 * 1000,
  });

  return {
    entry: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useCreateActivityData() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const organizationId = useGHGOrganizationId();

  return useMutation<ActivityDataResponse, Error, CreateActivityDataInput>({
    mutationFn: async (data) => {
      if (!organizationId) {
        throw new Error('Organization not found');
      }
      return createActivityData(getToken, organizationId, data);
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: activityDataKeys.all,
      });
      // When activity data is created with a taskId, the backend records a
      // contribution — invalidate task queries so lists reflect the new entry.
      if (variables.taskId) {
        void queryClient.invalidateQueries({ queryKey: ['tasks'] });
        void queryClient.invalidateQueries({
          queryKey: ['task-contributions', organizationId, variables.taskId],
        });
        void queryClient.invalidateQueries({ queryKey: ['task-history'] });
      }
    },
  });
}

export function useUpdateActivityData() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const organizationId = useGHGOrganizationId();

  return useMutation<
    ActivityDataResponse,
    Error,
    { entryId: string; data: UpdateActivityDataInput }
  >({
    mutationFn: async ({ entryId, data }) => {
      if (!organizationId) {
        throw new Error('Organization not found');
      }
      return updateActivityData(getToken, organizationId, entryId, data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: activityDataKeys.all,
      });
    },
  });
}

export function useDeleteActivityData() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const organizationId = useGHGOrganizationId();

  return useMutation<undefined, Error, string>({
    mutationFn: async (entryId) => {
      if (!organizationId) {
        throw new Error('Organization not found');
      }
      await deleteActivityData(getToken, organizationId, entryId);
      return undefined;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: activityDataKeys.all,
      });
    },
  });
}

export function useActivityEstimatePreview(
  request: ActivityEstimatePreviewInput | null,
  options?: { enabled?: boolean; debounceMs?: number }
) {
  const { getToken, isSignedIn } = useAuth();
  const organizationId = useGHGOrganizationId();
  const debouncedRequest = useDebouncedNullableValue(
    request,
    options?.debounceMs ?? 500
  );

  const query = useQuery({
    queryKey: activityDataKeys.preview(organizationId ?? '', debouncedRequest),
    queryFn: async ({ signal }) => {
      if (!organizationId || !debouncedRequest) {
        throw new Error('Organization or preview request not found');
      }
      return previewActivityEstimate(getToken, organizationId, debouncedRequest, {
        signal,
      });
    },
    enabled:
      (isSignedIn ?? false) &&
      !!organizationId &&
      !!debouncedRequest &&
      (options?.enabled ?? true),
    staleTime: 15 * 1000,
  });

  return query;
}

function useDebouncedNullableValue<T>(
  value: T | null,
  delayMs: number
): T | null {
  const [debouncedValue, setDebouncedValue] = useState<T | null>(value);

  useEffect(() => {
    if (value === null || delayMs <= 0) {
      setDebouncedValue(value);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setDebouncedValue(value);
    }, delayMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [value, delayMs]);

  return debouncedValue;
}
